import * as React from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { Plus } from 'lucide-react'

import { Button, Option, Select, Textarea } from '@/components/ui'
import { ResponseRoleList } from '@/schema'
import { cn } from '@/utils'

import {
  EMPTY_ADD_USERS_ROW,
  EStepperType,
  FIELD_LIMITS,
  INITIAL_ADD_USERS_FORM_DATA,
} from '../../constants'
import { useUserManagementStore } from '../../store'
import { IAddUsersFormData, IInviteUser } from '../../types'

import { InviteUserFormRow } from './InviteUserFormRow'

type StepAddUsersProps = {
  roles: ResponseRoleList[]
  availableRoles: Option[]
  stepId: string
  teamMemberRole: ResponseRoleList | undefined
  orgAdminRole: ResponseRoleList | undefined
}

const StepAddUsers: React.FC<StepAddUsersProps> = ({
  roles,
  availableRoles,
  stepId,
  teamMemberRole,
  orgAdminRole,
}) => {
  const { isInviteModalOpen, invitationErrorsByIndex, setAddUsersFormData } =
    useUserManagementStore()

  // Whether the step is active
  const isActive = React.useMemo(() => {
    return stepId === EStepperType.ADD_USERS
  }, [stepId])

  // Initialize form with React Hook Form
  const { control, reset, setValue, watch } = useForm<IAddUsersFormData>({
    defaultValues: INITIAL_ADD_USERS_FORM_DATA,
    mode: 'onChange',
  })

  // Reset form when dialog closes
  React.useEffect(() => {
    if (!isInviteModalOpen && !isActive) {
      // Reset form data in form
      reset(INITIAL_ADD_USERS_FORM_DATA)
      // Reset form data in store
      setAddUsersFormData(INITIAL_ADD_USERS_FORM_DATA)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isInviteModalOpen])

  React.useEffect(() => {
    if (roles.length && teamMemberRole) {
      setValue('roleId', teamMemberRole.id)
      setAddUsersFormData({ roleId: teamMemberRole.id })
    }
  }, [roles.length, setAddUsersFormData, setValue, teamMemberRole])

  // useFieldArray for dynamic user rows
  const { fields, append, remove } = useFieldArray({ control, name: 'users' })

  // Watch all users for validation
  const users = watch('users')

  // Add another user row
  const handleAddUser = React.useCallback(() => {
    if (!isActive) return

    // Add an empty user row
    const emptyUser = { ...EMPTY_ADD_USERS_ROW, uuid: crypto.randomUUID() }
    append(emptyUser)
    setAddUsersFormData({ users: [...users, emptyUser] })
  }, [append, isActive, setAddUsersFormData, users])

  // Remove user row
  const handleRemoveUser = React.useCallback(
    (index: number) => {
      if (!isActive) return

      if (index !== -1) remove(index)

      // Remove the user row from the form and store
      setAddUsersFormData({
        users: users.filter((user) => user.uuid !== fields[index].uuid),
      })
      // Clear the invitation error for the removed user
      delete invitationErrorsByIndex[index]
    },
    [
      fields,
      invitationErrorsByIndex,
      isActive,
      remove,
      setAddUsersFormData,
      users,
    ]
  )

  // Update user entry (only the user at index; keep others unchanged)
  const handleUpdateUser = React.useCallback(
    (updates: Partial<IInviteUser>, index: number) => {
      if (!isActive) return

      // Update the user entry
      const nextUser = { ...users[index], ...updates }

      // Update the user entry in the form
      setValue(`users.${index}`, nextUser, { shouldValidate: true })
      setAddUsersFormData({
        users: users.map((user, i) => (i === index ? nextUser : user)),
      })
    },
    [isActive, setAddUsersFormData, setValue, users]
  )

  return (
    <form className={cn('space-y-xl', !isActive && 'hidden')}>
      {/* User form rows */}
      <div>
        <div className="flex gap-2 items-end">
          <div className="flex gap-4 flex-[1_1_auto]">
            {/* First name */}
            <div className="w-1/4">
              <span className="text-sm font-medium text-secondary-700">
                First name
              </span>
            </div>

            {/* Last name */}
            <div className="w-1/4">
              <span className="text-sm font-medium text-secondary-700">
                Last name
              </span>
            </div>

            {/* Email */}
            <div className="w-1/2">
              <span className="text-sm font-medium text-secondary-700">
                Email
              </span>
            </div>
          </div>
          <div className="flex-[0_0_40px] w-10" />
        </div>
        <div className="space-y-2 max-h-56 overflow-y-auto overflow-x-hidden -m-1 mt-1.5 p-1">
          {fields.map((field, index) => (
            <InviteUserFormRow
              key={field.uuid}
              entry={users[index]}
              onUpdate={(entry) => handleUpdateUser(entry, index)}
              onRemove={() => handleRemoveUser(index)}
              invitationError={invitationErrorsByIndex[index]}
              disableRemove={fields.length <= 1}
              index={index}
            />
          ))}
        </div>
      </div>

      <Button
        variant="link"
        className="no-underline text-sm"
        leadingIcon={<Plus size={20} />}
        onClick={handleAddUser}
      >
        Add another user
      </Button>

      <div className="border-t border-secondary" />

      {/* Role */}
      <div className="space-y-1.5">
        <label
          htmlFor="role"
          className="text-sm font-medium text-secondary-700"
        >
          Role
        </label>

        <Controller
          name="roleId"
          control={control}
          rules={{ required: 'Role is required' }}
          render={({ field }) => (
            <Select
              value={field.value}
              onChange={(val) => {
                if (!val) return

                // Update the form data
                const formData: Partial<IAddUsersFormData> = { roleId: val }

                // If the new role is an organization admin, clear the assigned scopes
                const isOrgAdmin = val === orgAdminRole?.id
                if (isOrgAdmin) formData.assignedProjects = []

                field.onChange(val)
                setAddUsersFormData(formData)
              }}
              fullWidth
              placeholder="Select role"
              options={availableRoles}
              disabled={!roles.length}
            />
          )}
        />
      </div>

      {/* Message (optional) */}
      <div className="space-y-1.5">
        <label
          htmlFor="message"
          className="text-sm font-medium text-secondary-700"
        >
          Message{' '}
          <span className="font-regular text-quaternary-500">(Optional)</span>
        </label>

        <Controller
          name="message"
          control={control}
          render={({ field }) => (
            <Textarea
              id="message"
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Enter message"
              maxLength={FIELD_LIMITS.MESSAGE_MAX_LENGTH}
              rows={4}
              className="py-3 px-3.5 bg-primary border-primary text-sm resize-none"
            />
          )}
        />
      </div>
    </form>
  )
}

const MemoizedStepAddUsers = React.memo(StepAddUsers)
MemoizedStepAddUsers.displayName = 'StepAddUsers'

export { MemoizedStepAddUsers as StepAddUsers }
